import React, { useEffect, useRef } from "react";

/** 動き出す前と、出しきったあとの静止時間 */
const HOLD_MS = 2000;

/** 流す速さ。速いと読めず、遅いと一周が長すぎる */
const SPEED_PX_PER_SEC = 40;

/**
 * 入りきらないときだけ横に流す。
 *
 * 行先番号は「22B」で済むこともあれば
 * 「(間合い)[ループ]22T→22B」のように長いこともある。列幅を長いほうに
 * 合わせると短い便ばかりの画面で無駄に空き、切り詰めると長い便が読めない。
 * **入るなら止めたまま、あふれる時だけ流す。**
 *
 * 動きは 2 秒静止 → 左へ流す → 出しきって 2 秒静止 → 瞬時に戻る、の繰り返し。
 * 折り返しで右へ戻すと、読んでいる途中に文字が逆走して読みにくい。
 *
 * **CSS だけでは書けない。** 移動量が要素の幅に依るので、キーフレームの
 * 位置を測ってから決める必要がある。Web Animations API で組み立てる。
 */
export default function MarqueeText({ text }) {
  const boxRef = useRef(null);
  const textRef = useRef(null);

  useEffect(() => {
    const box = boxRef.current;
    const el = textRef.current;
    if (!box || !el) {
      return undefined;
    }

    let animation = null;

    const apply = () => {
      animation?.cancel();
      animation = null;

      // scrollWidth / clientWidth は整数に丸められるので、ぴったり動かすと
      // 端数のぶん最後の 1px が欠ける。1px だけ余分に送る
      const over = el.scrollWidth - box.clientWidth + 1;
      if (over <= 2) {
        return; // 入っている。動かす必要がない
      }

      // 動きを減らす設定の人には出さない。読めれば十分で、動く必要はない
      if (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) {
        return;
      }

      const scrollMs = (over / SPEED_PX_PER_SEC) * 1000;
      const total = HOLD_MS * 2 + scrollMs;

      animation = el.animate(
        [
          { transform: "translateX(0)", offset: 0 },
          { transform: "translateX(0)", offset: HOLD_MS / total },
          { transform: `translateX(${-over}px)`, offset: (HOLD_MS + scrollMs) / total },
          { transform: `translateX(${-over}px)`, offset: 1 },
        ],
        { duration: total, iterations: Infinity, easing: "linear" }
      );
    };

    apply();

    // 画面幅が変わると入るかどうかも変わる
    const observer = new ResizeObserver(apply);
    observer.observe(box);

    return () => {
      observer.disconnect();
      animation?.cancel();
    };
  }, [text]);

  // **枠線や余白をこの箱に持たせないこと。**
  // 切り取りは content box の縁で起きるので、枠線を内側に持つと最後の文字が
  // その下に隠れる。見た目は呼び出し側の親に付けて、ここは切って動かすだけ。
  return (
    <span className="anl-marquee" ref={boxRef}>
      <span className="anl-marquee__text" ref={textRef}>
        {text}
      </span>
    </span>
  );
}
